"use client";

import { useEffect, useState } from "react";

type PremiumRow = {
  symbol: string;
  score: number;
  next_day_up_prob: number;
  confidence: number;
  expected_move_pct: number;
  signal: string;
  risk_flag: string;
  price: number;
  traded_value_mn: number | null;
  uin_pct: number | null;
  off_market_value_mn: number | null;
  off_market_premium_pct: number | null;
  leverage_oi_score: number;
  reasons: string[];
};

type PremiumSummary = {
  total_candidates: number;
  long_count: number;
  watch_count: number;
  avoid_count: number;
  avg_long_prob: number;
  avg_long_expected_move_pct: number;
};

type PremiumPayload = {
  as_of: string;
  top_long: PremiumRow[];
  watchlist: PremiumRow[];
  risk_avoid: PremiumRow[];
  summary: PremiumSummary;
};

type PremiumDashboard = {
  ok: boolean;
  generated_at: string;
  payload: PremiumPayload;
};

const fmt = (v: number | null | undefined, d = 2) =>
  typeof v === "number" && isFinite(v)
    ? v.toLocaleString(undefined, { maximumFractionDigits: d })
    : "—";

const fmtPct = (v: number | null | undefined, d = 1) =>
  typeof v === "number" && isFinite(v) ? `${(v * 100).toFixed(d)}%` : "—";

const riskColor = (flag: string) => {
  const f = flag?.toLowerCase();
  if (f === "normal") return "var(--color-success)";
  if (f?.includes("high") || f?.includes("caution")) return "#f59e0b";
  return "var(--color-danger)";
};

const TABLE_HEADERS = ["Symbol", "Score", "Prob", "Conf", "Exp Move", "UIN %", "Off-Mkt", "Lev OI", "Risk", "Reasons"];

function PremiumTable({ rows, accent }: { rows: PremiumRow[]; accent: string }) {
  if (!rows.length) return <div style={{ color: "var(--color-text-muted)", padding: "1rem 0" }}>No data.</div>;
  return (
    <div style={{ overflowX: "auto", borderRadius: "12px", border: "1px solid rgba(148,163,184,0.18)", background: "rgba(15,23,42,0.55)" }}>
      <table className="data-table" style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {TABLE_HEADERS.map((h, i) => (
              <th key={h} style={{
                padding: "0.8rem 0.85rem",
                fontSize: "0.78rem",
                textTransform: "uppercase",
                letterSpacing: "0.07em",
                color: "var(--color-text-muted)",
                textAlign: i === 0 || i === TABLE_HEADERS.length - 1 ? "left" : "right",
                whiteSpace: "nowrap",
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.symbol} style={{ background: idx % 2 === 0 ? "rgba(255,255,255,0.02)" : "transparent" }}>
              <td style={{ padding: "0.8rem 0.85rem", fontWeight: 700 }}>
                <span style={{ color: accent }}>{row.symbol}</span>
                <div style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", fontWeight: 400 }}>Rs. {fmt(row.price)}</div>
              </td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right", fontWeight: 700, color: row.score >= 80 ? "var(--color-success)" : row.score >= 60 ? "#f59e0b" : "var(--color-text)" }}>
                {fmt(row.score, 0)}
              </td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right", fontWeight: 700, color: "#38bdf8" }}>
                {fmtPct(row.next_day_up_prob)}
              </td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right" }}>{fmt(row.confidence, 0)}%</td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right", color: "var(--color-success)" }}>
                {typeof row.expected_move_pct === "number" ? `${row.expected_move_pct.toFixed(1)}%` : "—"}
              </td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right" }}>{fmt(row.uin_pct, 1)}%</td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right", color: "var(--color-text-muted)" }}>
                {row.off_market_value_mn != null ? `${fmt(row.off_market_value_mn, 1)}M` : "—"}
                {row.off_market_premium_pct != null ? <span style={{ fontSize: "0.75rem", marginLeft: "0.25rem", color: row.off_market_premium_pct > 0 ? "var(--color-success)" : "var(--color-danger)" }}>
                  {row.off_market_premium_pct > 0 ? "+" : ""}{fmt(row.off_market_premium_pct, 1)}%
                </span> : null}
              </td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right" }}>{fmt(row.leverage_oi_score, 1)}</td>
              <td style={{ padding: "0.8rem 0.85rem", textAlign: "right" }}>
                <span style={{
                  display: "inline-block", padding: "0.15rem 0.5rem", borderRadius: "999px",
                  fontSize: "0.72rem", fontWeight: 700,
                  color: riskColor(row.risk_flag),
                  background: `${riskColor(row.risk_flag)}18`,
                  border: `1px solid ${riskColor(row.risk_flag)}35`,
                }}>
                  {row.risk_flag || "—"}
                </span>
              </td>
              <td style={{ padding: "0.8rem 0.85rem", maxWidth: "220px" }}>
                <div style={{ display: "flex", gap: "0.3rem", flexWrap: "wrap" }}>
                  {(row.reasons ?? []).slice(0, 2).map((r, i) => (
                    <span key={i} style={{
                      fontSize: "0.7rem", padding: "0.1rem 0.45rem", borderRadius: "4px",
                      background: "rgba(148,163,184,0.1)", color: "rgba(148,163,184,0.8)",
                    }}>{r}</span>
                  ))}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function PremiumScannerPage() {
  const [data, setData] = useState<PremiumDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<"long" | "watch" | "avoid">("long");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch("/api/outputs/premium_next_day_dashboard.json", { cache: "no-store" });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = (await res.json()) as PremiumDashboard;
        setData(json);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load premium data.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const p = data?.payload;
  const s = p?.summary;
  const asOf = p?.as_of ?? data?.generated_at;
  const scanDate = asOf ? new Date(asOf).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "N/A";

  const TABS: { key: typeof tab; label: string; color: string; count: number | undefined }[] = [
    { key: "long", label: "Top Long", color: "var(--color-success)", count: p?.top_long.length },
    { key: "watch", label: "Watchlist", color: "#38bdf8", count: p?.watchlist.length },
    { key: "avoid", label: "Risk Avoid", color: "var(--color-danger)", count: p?.risk_avoid.length },
  ];

  return (
    <>
      <section className="page-header page-header--accent">
        <div className="page-header-container">
          <h1 className="page-title">Premium Next Day Scanner</h1>
          <p className="page-subtitle">
            Composite-scored trade candidates combining price action, UIN settlement flow, off-market data, and leverage OI.
          </p>
        </div>
      </section>

      <section className="strategy-detail-section">
        <div className="strategy-detail-container">

          {/* Summary cards */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: "0.75rem", marginBottom: "1.5rem" }}>
            {[
              { label: "As Of", value: scanDate },
              { label: "Screened", value: s ? s.total_candidates.toLocaleString() : "—" },
              { label: "Top Longs", value: s ? String(s.long_count) : "—" },
              { label: "Avg Prob", value: s ? `${(s.avg_long_prob * 100).toFixed(1)}%` : "—" },
              { label: "Avg Exp Move", value: s ? `${s.avg_long_expected_move_pct.toFixed(1)}%` : "—" },
            ].map((c) => (
              <div key={c.label} style={{
                background: "rgba(15,23,42,0.6)", border: "1px solid rgba(148,163,184,0.15)",
                borderRadius: "10px", padding: "0.85rem 1rem",
              }}>
                <div style={{ fontSize: "0.72rem", color: "rgba(148,163,184,0.6)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: "0.3rem" }}>{c.label}</div>
                <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>{c.value}</div>
              </div>
            ))}
          </div>

          {/* Tab switcher */}
          <div style={{ display: "flex", gap: "0.5rem", marginBottom: "1.1rem", flexWrap: "wrap" }}>
            {TABS.map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  padding: "0.45rem 1rem", borderRadius: "999px", fontWeight: 700, fontSize: "0.85rem", cursor: "pointer",
                  border: tab === t.key ? `1px solid ${t.color}55` : "1px solid rgba(148,163,184,0.22)",
                  background: tab === t.key ? `${t.color}18` : "rgba(15,23,42,0.5)",
                  color: tab === t.key ? t.color : "var(--color-text)",
                }}
              >
                {t.label}{t.count != null ? ` (${t.count})` : ""}
              </button>
            ))}
          </div>

          {loading && <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-text-muted)" }}>Loading premium data...</div>}
          {error && <div style={{ padding: "1rem", borderRadius: "12px", border: "1px solid rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "var(--color-danger)" }}>{error}</div>}

          {!loading && !error && p && (
            <>
              {tab === "long" && <PremiumTable rows={p.top_long} accent="var(--color-success)" />}
              {tab === "watch" && <PremiumTable rows={p.watchlist} accent="#38bdf8" />}
              {tab === "avoid" && <PremiumTable rows={p.risk_avoid} accent="var(--color-danger)" />}
            </>
          )}
        </div>
      </section>
    </>
  );
}
